/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useState, useEffect, useMemo, useCallback } from 'react';
import type { TaxonomyCodeDto, SuggestedTaxonomyDto, TaxonomyFilterOptionsDto } from '@shared/types';
import { fetchAllTaxonomyCodes, fetchSuggestedTaxonomies, fetchTaxonomyFilterOptions } from '../../services/formularyApi';
import { Tooltip } from '../layout/Tooltip';

interface TaxonomyBrowserProps {
  /** Called when the user clicks Assign on a taxonomy code */
  onSelect: (taxonomy: TaxonomyCodeDto) => void;
  /** Called with all checked taxonomies when the user clicks "Assign Selected" */
  onBatchSelect?: (taxonomies: TaxonomyCodeDto[]) => void;
  /** If provided, loads and highlights suggested taxonomies for this formula */
  formulaSetId?: string;
  /** Maximum items to display at once */
  maxDisplay?: number;
  /** Whether a save operation is in progress */
  isSaving?: boolean;
  /** Fill available parent height instead of using fixed heights */
  fillHeight?: boolean;
  /** Taxonomy set IDs to exclude from the list (already assigned) */
  excludeSetIds?: Set<string>;
  /** Disable all action buttons (batch mode) */
  disabled?: boolean;
}

export function TaxonomyBrowser({ onSelect, onBatchSelect, formulaSetId, maxDisplay = 100, isSaving, fillHeight, excludeSetIds, disabled = false }: TaxonomyBrowserProps) {
  const [allCodes, setAllCodes] = useState<TaxonomyCodeDto[]>([]);
  const [filterOpts, setFilterOpts] = useState<TaxonomyFilterOptionsDto | null>(null);
  const [suggestions, setSuggestions] = useState<SuggestedTaxonomyDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filters
  const [textFilter, setTextFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [groupingFilter, setGroupingFilter] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');
  const [specializationFilter, setSpecializationFilter] = useState('');
  const [tab, setTab] = useState<'all' | 'suggested'>('all');

  // Expanded detail
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);

  // Multi-select checkboxes
  const [checkedSetIds, setCheckedSetIds] = useState<Set<string>>(new Set());

  // Drop any checked items that just got assigned (excludeSetIds changed)
  useEffect(() => {
    if (!excludeSetIds || checkedSetIds.size === 0) return;
    setCheckedSetIds((prev) => {
      const next = new Set(prev);
      for (const id of prev) if (excludeSetIds.has(id)) next.delete(id);
      return next.size === prev.size ? prev : next;
    });
  }, [excludeSetIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load all taxonomy codes + filter options once
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    Promise.all([fetchAllTaxonomyCodes(), fetchTaxonomyFilterOptions()])
      .then(([codesRes, optsRes]) => {
        if (!cancelled) {
          setAllCodes(codesRes.data);
          setFilterOpts(optsRes.data);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Load suggestions when formulaSetId is provided
  useEffect(() => {
    if (!formulaSetId) { setSuggestions([]); return; }
    let cancelled = false;
    fetchSuggestedTaxonomies(formulaSetId)
      .then((res) => {
        if (!cancelled) {
          setSuggestions(res.data);
          if (res.data.length > 0) setTab('suggested');
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [formulaSetId]);

  const suggestedSetIds = useMemo(() => new Set(suggestions.map((s) => s.setId)), [suggestions]);

  // Cascading filter options derived from data
  const groupingOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of allCodes) {
      if (sectionFilter && c.section !== sectionFilter) continue;
      if (c.grouping) set.add(c.grouping);
    }
    return [...set].sort();
  }, [allCodes, sectionFilter]);

  const classificationOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of allCodes) {
      if (sectionFilter && c.section !== sectionFilter) continue;
      if (groupingFilter && c.grouping !== groupingFilter) continue;
      if (c.classification) set.add(c.classification);
    }
    return [...set].sort();
  }, [allCodes, sectionFilter, groupingFilter]);

  const specializationOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of allCodes) {
      if (sectionFilter && c.section !== sectionFilter) continue;
      if (groupingFilter && c.grouping !== groupingFilter) continue;
      if (classificationFilter && c.classification !== classificationFilter) continue;
      if (c.specialization) set.add(c.specialization);
    }
    return [...set].sort();
  }, [allCodes, sectionFilter, groupingFilter, classificationFilter]);

  // Reset child filters when parent changes
  useEffect(() => {
    if (groupingFilter && !groupingOptions.includes(groupingFilter)) setGroupingFilter('');
  }, [groupingOptions, groupingFilter]);
  useEffect(() => {
    if (classificationFilter && !classificationOptions.includes(classificationFilter)) setClassificationFilter('');
  }, [classificationOptions, classificationFilter]);
  useEffect(() => {
    if (specializationFilter && !specializationOptions.includes(specializationFilter)) setSpecializationFilter('');
  }, [specializationOptions, specializationFilter]);

  const hasFilters = !!(textFilter || sectionFilter || groupingFilter || classificationFilter || specializationFilter);

  const filtered = useMemo(() => {
    // Suggested tab always shows results (already curated)
    if (tab === 'suggested' && suggestions.length > 0) {
      const lc = textFilter.toLowerCase();
      return suggestions.filter((c) => {
        if (excludeSetIds?.has(c.setId)) return false;
        if (sectionFilter && c.section !== sectionFilter) return false;
        if (groupingFilter && c.grouping !== groupingFilter) return false;
        if (classificationFilter && c.classification !== classificationFilter) return false;
        if (specializationFilter && c.specialization !== specializationFilter) return false;
        if (lc && !c.code.toLowerCase().includes(lc) && !c.displayName.toLowerCase().includes(lc)) return false;
        return true;
      }).slice(0, maxDisplay);
    }
    // "All" tab — show all codes (filtered if active, first page otherwise)
    const lc = textFilter.toLowerCase();
    return allCodes.filter((c) => {
      if (excludeSetIds?.has(c.setId)) return false;
      if (sectionFilter && c.section !== sectionFilter) return false;
      if (groupingFilter && c.grouping !== groupingFilter) return false;
      if (classificationFilter && c.classification !== classificationFilter) return false;
      if (specializationFilter && c.specialization !== specializationFilter) return false;
      if (lc && !c.code.toLowerCase().includes(lc) && !c.displayName.toLowerCase().includes(lc)) return false;
      return true;
    }).slice(0, maxDisplay);
  }, [allCodes, suggestions, textFilter, sectionFilter, groupingFilter, classificationFilter, specializationFilter, tab, maxDisplay, excludeSetIds]);

  const selectedCode = useMemo(
    () => (selectedSetId ? allCodes.find((c) => c.setId === selectedSetId) ?? null : null),
    [allCodes, selectedSetId]
  );

  const handleAssign = useCallback((tax: TaxonomyCodeDto) => {
    onSelect(tax);
  }, [onSelect]);

  function toggleCheck(e: React.MouseEvent, tax: TaxonomyCodeDto) {
    e.stopPropagation();
    setCheckedSetIds((prev) => {
      const next = new Set(prev);
      if (next.has(tax.setId)) next.delete(tax.setId);
      else next.add(tax.setId);
      return next;
    });
  }

  function handleBatchAssign() {
    if (checkedSetIds.size === 0) return;
    const idSet = checkedSetIds;
    const resolved = [...allCodes, ...suggestions].reduce<Map<string, TaxonomyCodeDto>>((m, t) => {
      if (idSet.has(t.setId) && !m.has(t.setId)) m.set(t.setId, t);
      return m;
    }, new Map());
    const items = [...resolved.values()];
    setCheckedSetIds(new Set());
    if (onBatchSelect) {
      onBatchSelect(items);
    } else {
      for (const tax of items) onSelect(tax);
    }
  }

  function clearFilters() {
    setTextFilter('');
    setSectionFilter('');
    setGroupingFilter('');
    setClassificationFilter('');
    setSpecializationFilter('');
  }

  if (isLoading && allCodes.length === 0) {
    return <div className="text-xs text-gray-400 py-3 text-center">Loading taxonomy codes...</div>;
  }

  return (
    <div className={`flex flex-col gap-2 ${fillHeight ? 'h-full' : ''}`}>
      {/* Tabs + filters — single compact row */}
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        <div className="flex rounded-lg border border-gray-200 text-xs overflow-hidden">
          <Tooltip text="Browse all available taxonomy codes">
            <button
              onClick={() => setTab('all')}
              className={`px-2.5 py-1 font-medium transition-colors ${tab === 'all' ? 'bg-dds-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              All ({allCodes.length})
            </button>
          </Tooltip>
          {suggestions.length > 0 && (
            <Tooltip text="Codes assigned to formulas with shared ingredients — ranked by match count">
              <button
                onClick={() => setTab('suggested')}
                className={`px-2.5 py-1 font-medium border-l border-gray-200 transition-colors ${tab === 'suggested' ? 'bg-dds-teal-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                Suggested ({suggestions.length})
              </button>
            </Tooltip>
          )}
        </div>

        <input
          type="text"
          aria-label="Search taxonomy codes"
          value={textFilter}
          onChange={(e) => setTextFilter(e.target.value)}
          placeholder="Search code or name..."
          className="text-xs border border-gray-200 rounded-md px-2 py-1 w-40 focus:outline-none focus:ring-1 focus:ring-dds-blue-500"
        />
        <select aria-label="Section filter" value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)} className="text-xs border border-gray-200 rounded-md px-1.5 py-1">
          <option value="">Section</option>
          {(filterOpts?.sections ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select aria-label="Grouping filter" value={groupingFilter} onChange={(e) => setGroupingFilter(e.target.value)} className="text-xs border border-gray-200 rounded-md px-1.5 py-1">
          <option value="">Grouping</option>
          {groupingOptions.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select aria-label="Classification filter" value={classificationFilter} onChange={(e) => setClassificationFilter(e.target.value)} className="text-xs border border-gray-200 rounded-md px-1.5 py-1 max-w-[160px]">
          <option value="">Classification</option>
          {classificationOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {specializationOptions.length > 0 && (
          <select aria-label="Specialization filter" value={specializationFilter} onChange={(e) => setSpecializationFilter(e.target.value)} className="text-xs border border-gray-200 rounded-md px-1.5 py-1 max-w-[160px]">
            <option value="">Specialization</option>
            {specializationOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {hasFilters && (
          <button onClick={clearFilters} className="text-[11px] text-gray-400 hover:text-gray-600 underline">Clear</button>
        )}
        <span className="text-[11px] text-gray-400 ml-auto">{filtered.length} shown</span>
      </div>

      {/* Split view: list + detail — fills remaining height */}
      <div className={`flex border border-gray-200 rounded-lg overflow-hidden ${fillHeight ? 'flex-1 min-h-0' : ''}`} style={fillHeight ? undefined : { minHeight: 260 }}>
        {/* Code list */}
        <div className={`w-1/2 border-r border-gray-200 overflow-y-auto ${fillHeight ? '' : 'max-h-72'}`}>
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-gray-400">
              {hasFilters
                ? 'No codes match the current filters'
                : tab === 'suggested'
                  ? 'No suggested codes available'
                  : 'No taxonomy codes loaded'}
            </div>
          ) : (
            <ul>
              {filtered.map((tax) => {
                const isSuggested = suggestedSetIds.has(tax.setId);
                const suggestion = isSuggested ? suggestions.find((s) => s.setId === tax.setId) : null;
                const isSelected = selectedSetId === tax.setId;

                const isChecked = checkedSetIds.has(tax.setId);

                return (
                  <li
                    key={tax.setId}
                    onClick={() => setSelectedSetId(isSelected ? null : tax.setId)}
                    className={`px-3 py-1.5 cursor-pointer text-sm border-b border-gray-50 transition-colors ${
                      isSelected ? 'bg-dds-blue-50 border-l-2 border-l-dds-blue-500' : ''
                    } ${isSuggested && !isSelected ? 'bg-teal-50/40' : ''} ${!isSelected ? 'hover:bg-gray-50' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        {!disabled && (
                          <input
                            type="checkbox"
                            aria-label={`Select ${tax.code} ${tax.displayName}`}
                            checked={isChecked}
                            onClick={(e) => toggleCheck(e, tax)}
                            onChange={() => {}}
                            className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-dds-blue-500 cursor-pointer"
                          />
                        )}
                        <div className="min-w-0">
                          <span className="font-medium text-dds-blue-600 text-xs">{tax.code}</span>
                          <span className="text-gray-400 mx-1">·</span>
                          <span className="text-gray-700 text-xs truncate">{tax.displayName}</span>
                        </div>
                      </div>
                      {suggestion && (
                        <span className="text-[10px] bg-teal-100 text-teal-700 font-medium px-1.5 py-0.5 rounded-full ml-2 shrink-0">
                          {suggestion.sharedIngredientCount} shared
                        </span>
                      )}
                    </div>
                    {tax.grouping && (
                      <div className={`text-[10px] text-gray-400 truncate ${!disabled ? 'pl-5' : ''}`}>
                        {tax.grouping}{tax.classification ? ` › ${tax.classification}` : ''}{tax.specialization ? ` › ${tax.specialization}` : ''}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Detail pane */}
        <div className={`w-1/2 p-3 bg-gray-50/50 overflow-y-auto ${fillHeight ? '' : 'max-h-72'}`}>
          {selectedCode ? (
            <div className="flex flex-col h-full">
              <div className="flex-1 space-y-2 min-h-0 overflow-y-auto">
                <div>
                  <div className="text-base font-bold text-dds-blue-600">{selectedCode.code}</div>
                  <div className="text-sm text-gray-900 font-medium">{selectedCode.displayName}</div>
                </div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                  {selectedCode.section && (
                    <div><span className="text-gray-400">Section</span><div className="font-medium text-gray-700">{selectedCode.section}</div></div>
                  )}
                  {selectedCode.grouping && (
                    <div><span className="text-gray-400">Grouping</span><div className="font-medium text-gray-700">{selectedCode.grouping}</div></div>
                  )}
                  {selectedCode.classification && (
                    <div><span className="text-gray-400">Classification</span><div className="font-medium text-gray-700">{selectedCode.classification}</div></div>
                  )}
                  {selectedCode.specialization && (
                    <div><span className="text-gray-400">Specialization</span><div className="font-medium text-gray-700">{selectedCode.specialization}</div></div>
                  )}
                </div>

                {selectedCode.definition && (
                  <div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wide">Definition</div>
                    <p className="text-xs text-gray-600 leading-relaxed">{selectedCode.definition}</p>
                  </div>
                )}

                {selectedCode.notes && (
                  <div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wide">Notes</div>
                    <p className="text-xs text-gray-600 leading-relaxed">{selectedCode.notes}</p>
                  </div>
                )}
              </div>

              <button
                onClick={() => handleAssign(selectedCode)}
                disabled={isSaving || disabled || excludeSetIds?.has(selectedCode.setId)}
                className="mt-2 w-full bg-dds-blue-500 text-white py-1.5 rounded-lg text-sm font-medium hover:bg-dds-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                {isSaving ? 'Assigning...' : disabled ? 'Batch Mode — View Only' : excludeSetIds?.has(selectedCode.setId) ? 'Already Assigned' : `Assign ${selectedCode.code}`}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-xs text-gray-400">
              Select a code to view details
            </div>
          )}
        </div>
      </div>

      {!disabled && checkedSetIds.size > 0 && (
        <div className="flex items-center gap-3 bg-dds-blue-50 border border-dds-blue-200 rounded-lg px-3 py-2 shrink-0">
          <span className="text-xs text-dds-blue-700 font-medium flex-1">
            {checkedSetIds.size} {checkedSetIds.size === 1 ? 'taxonomy' : 'taxonomies'} selected
          </span>
          <button
            onClick={() => setCheckedSetIds(new Set())}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
          <button
            onClick={handleBatchAssign}
            disabled={isSaving}
            className="bg-dds-blue-500 text-white px-3 py-1 rounded-md text-xs font-medium hover:bg-dds-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Assigning...' : `Assign Selected (${checkedSetIds.size})`}
          </button>
        </div>
      )}

      {filtered.length >= maxDisplay && (
        <div className="text-[11px] text-gray-400 text-center shrink-0">
          Showing first {maxDisplay} — use filters to narrow results
        </div>
      )}
    </div>
  );
}
